/* eslint-disable no-empty-pattern */

import Vue from 'vue';
import {UserGroups} from './session';

export const UserRole = Object.freeze({
   BUSINESS_MANAGER: 'Business Manager',
   PROJECT_MANAGER: 'Project Manager',
   PROJECT_ASSOCIATE: 'Project Associate',
   EXTERNAL_PARTNER: 'External Partner',
});

export const PermissionResourceType = Object.freeze({
   EMPLOYEE_PAYROLL: 'EMPLOYEE_PAYROLL',
   GROSS_RECEIPTS: 'GROSS_RECEIPTS',
   CONTRACTOR_PAYMENTS: 'CONTRACTOR_PAYMENTS',
});

const derivePermissionFlags = (permissions = []) => {
   const hasPermission = (resourceType) =>
      !!permissions.find((permission) => permission.resourceType === resourceType)?.hasAccess;

   return {
      payroll: hasPermission(PermissionResourceType.EMPLOYEE_PAYROLL),
      revenue: hasPermission(PermissionResourceType.GROSS_RECEIPTS),
      contractorPayments: hasPermission(PermissionResourceType.CONTRACTOR_PAYMENTS),
   };
};

const derivePermissionEditState = (permissions = []) => {
   const payrollPermission = permissions.find(
      (permission) => permission.resourceType === PermissionResourceType.EMPLOYEE_PAYROLL
   );
   const revenuePermission = permissions.find(
      (permission) => permission.resourceType === PermissionResourceType.GROSS_RECEIPTS
   );
   const contractorPaymentsPermission = permissions.find(
      (permission) => permission.resourceType === PermissionResourceType.CONTRACTOR_PAYMENTS
   );

   return {
      permissionsExist: permissions.length > 0,
      permissions: {
         payroll: !!payrollPermission?.hasAccess,
         payrollId: payrollPermission?.id ?? '',
         revenue: !!revenuePermission?.hasAccess,
         revenueId: revenuePermission?.id ?? '',
         contractorPayments: !!contractorPaymentsPermission?.hasAccess,
         contractorPaymentsId: contractorPaymentsPermission?.id ?? '',
      },
   };
};

let userKey = 0;

const state = () => ({
   users: {},
   companyUsers: {},
   newUsers: [],
   profileImageCacheKeys: {},
   loading: false,
});

const getters = {
   users: (state) => Object.values(state.users),
   userMap: (state) => state.users,
   loading: (state) => state.loading,

   adminUsers: (state) => {
      return Object.values(state.users).filter((user) => user.group === UserGroups.ADMIN);
   },
   staffUsers: (state) => {
      return Object.values(state.users).filter((user) => user.group === UserGroups.STAFF);
   },
   customerUsers: (state) => {
      return Object.values(state.users).filter((user) => user.group === UserGroups.CUSTOMER);
   },
   smeUsers: (state) => {
      return Object.values(state.users).filter((user) => user.group === UserGroups.SME);
   },
   externalPartnerUsers: (state) => {
      return Object.values(state.users).filter(
         (user) => user.group === UserGroups.EXTERNAL_PARTNER
      );
   },
   companyUsers: (state) => state.companyUsers,
   newUsers: (state) => state.newUsers,
   profileImageCacheKey: (state) => (userId) => {
      return state.profileImageCacheKeys[userId] ?? 0;
   },
   currentUserPermissionFlags: (state, getters, rootState, rootGetters) => {
      return derivePermissionFlags(rootGetters.profile?.permissions || []);
   },
};

const mutations = {
   clear: (state) => {
      state.users = {};
      state.companyUsers = {};
      state.newUsers = [
         {
            firstName: null,
            lastName: null,
            email: null,
            title: null,
            department: null,
            subsidiary: null,
            key: userKey++,
         },
      ];
   },

   /** Store users associated with a company */
   setCompanyUsers: (state, {users}) => {
      users.forEach((user) => {
         Vue.set(state.companyUsers, user.id, user);
      });
   },

   setCompanyUser: (state, {user}) => {
      Vue.set(state.companyUsers, user.id, user);
   },

   /** Store users */
   setUsers: (state, {users, enabledOnly = false}) => {
      users.forEach((user) => {
         if (!enabledOnly || user.enabled) {
            Vue.set(state.users, user.id, user);
         }
      });
   },

   setUser: (state, {user}) => {
      Vue.set(state.users, user.id, user);
   },

   /** Add a blank user to the newUsers array */
   addNewUser: (state) => {
      state.newUsers.push({
         firstName: null,
         lastName: null,
         email: null,
         title: null,
         department: null,
         subsidiary: null,
         key: userKey++,
      });
   },

   /** Remove a user from newUsers */
   removeNewUser: (state, {index}) => {
      state.newUsers.splice(index, 1);
   },

   resetNewUsers(state) {
      state.newUsers = [
         {
            firstName: '',
            lastName: '',
            email: '',
            title: '',
            department: '',
         },
      ];
   },

   /** Update a value on a new user */
   updateNewUser: (state, {index, field, value}) => {
      state.newUsers[index][field] = value;
   },

   /** Increment the cache key for a user's profile image */
   refreshProfileImage: (state, {userId}) => {
      Vue.set(state.profileImageCacheKeys, userId, (state.profileImageCacheKeys[userId] ?? 0) + 1);
   },

   setLoading: (state, loading) => {
      state.loading = loading;
   },
};

const actions = {
   // Fetch the list of users
   async fetchUsers() {
      return (await this._vm.$http.get('/api/user')).data.results;
   },

   async loadUsers({commit}, {enabledOnly = false} = {}) {
      commit('setLoading', true);
      try {
         const users = (await this._vm.$httpNoStudy.get('/api/user')).data.results;
         commit('clear');
         commit('setUsers', {users, enabledOnly});
      } finally {
         commit('setLoading', false);
      }
   },

   // Fetch a user profile
   async fetchProfile({}, {id}) {
      return (await this._vm.$http.get(`/api/user/${id}`)).data;
   },

   // Create a user
   async createUser({}, {user}) {
      return (await this._vm.$httpNoStudy.post('/api/user', user)).data;
   },

   // Update a user profile
   async updateProfile({rootGetters, commit}, {id, profile}) {
      const newProfile = (await this._vm.$http.put(`/api/user/${id}`, profile)).data;
      if (id === rootGetters.profile.id) {
         commit('setProfile', newProfile, {root: true});
      }

      return newProfile;
   },

   // Enable a user
   async enableUser({commit}, {id}) {
      const userData = (await this._vm.$http.put(`/api/user/${id}/enable`)).data;
      commit('setUser', {user: userData});
      return userData;
   },

   // Disable a user
   async disableUser({state, commit}, {id, remove = false}) {
      const userData = (await this._vm.$http.put(`/api/user/${id}/disable`)).data;
      if (remove) {
         Vue.delete(state.users, id);
      } else {
         commit('setUser', {user: userData});
      }
      return userData;
   },

   // Reset a user's password
   async resetPassword({}, payload) {
      return (await this._vm.$http.post(`/api/user/${payload.id}/reset-password`)).data;
   },

   // Fetch a staff user's company roles
   async fetchUserRoles({}, payload) {
      return (await this._vm.$http.get(`/api/user/${payload.id}/roles`)).data;
   },

   /**
    * Load the client users of a company
    * @param {Object} payload
    * @param {string} payload.companyId - the ID of a company
    */
   async loadCompanyUsers({commit}, {companyId}) {
      const users = (await this._vm.$http.get(`/api/company/${companyId}/user`)).data.results;
      commit('setCompanyUsers', {users});
   },

   /**
    * POST the users in the newUsers array
    * @param {Object} payload
    */
   async saveNewUsers({commit, state}, {companyId, group = UserGroups.SME}) {
      const requests = state.newUsers.map((user, index) => {
         const payload = {
            ...user,
            companyId,
            group,
         };
         delete payload['key'];
         return this._vm.$httpNoStudy
            .post(`/api/user`, payload)
            .then((response) => {
               commit('setCompanyUser', {user: response.data});
               commit(
                  'showAlert',
                  {
                     msg: `Successfully saved user ${user.firstName} ${user.lastName}`,
                     variant: 'success',
                     seconds: 5,
                  },
                  {root: true}
               );
               return {user: response.data, index, response, error: null};
            })
            .catch((error) => {
               return {user, index, error, response: error.response};
            });
      });

      const results = await Promise.allSettled(requests);
      results
         .filter((result) => result.value.error === null)
         .reverse()
         .forEach((result) => {
            commit('removeNewUser', {index: result.value.index});
         });
      return results;
   },

   async uploadProfileImage({commit}, {canvas, userId}) {
      if (canvas) {
         const formData = new FormData();
         const blob = await new Promise((resolve) => canvas.toBlob(resolve));
         formData.append('image', blob);
         await this._vm.$http.post(`/api/user/${userId}/profile/image`, formData, {
            headers: {'Content-Type': 'multipart/form-data'},
         });
         commit('refreshProfileImage', {userId});
         commit('refreshProfileImage', null, {root: true});
      }
   },

   async fetchUserPermissions({}, {userId}) {
      return (await this._vm.$http.get(`/api/permissions?user_id=${userId}`)).data;
   },

   async fetchUserPermissionFlags({dispatch}, {userId}) {
      const permissions = await dispatch('fetchUserPermissions', {userId});
      return derivePermissionFlags(permissions);
   },

   async fetchUserPermissionEditState({dispatch}, {userId}) {
      const permissions = await dispatch('fetchUserPermissions', {userId});
      return derivePermissionEditState(permissions);
   },

   async createUserPermissions(
      {},
      {userId, payroll, revenue, contractorPayments = false, isInvitation = false}
   ) {
      return (
         await this._vm.$http.post('/api/permissions', {
            userId,
            resourceAccess: [
               {
                  rtype: 'EMPLOYEE_PAYROLL',
                  value: payroll,
               },
               {
                  rtype: 'GROSS_RECEIPTS',
                  value: revenue,
               },
               {
                  rtype: 'CONTRACTOR_PAYMENTS',
                  value: contractorPayments,
               },
            ],
            isInvitation,
         })
      ).data;
   },

   async updateUserPermission({}, {permissionId, userId, resourceType, hasAccess, createdBy}) {
      return (
         await this._vm.$http.put(`/api/permissions/${permissionId}`, {
            userId,
            resourceType,
            hasAccess,
            createdBy,
         })
      ).data;
   },
};

export default {
   namespaced: true,
   state,
   getters,
   mutations,
   actions,
};
